import { NavLink, Link, useLocation } from "react-router-dom";
import { Home, FolderOpen, MessageCircle, User, Images, ShoppingBag, HelpCircle, GraduationCap, Wand2 } from "lucide-react";
import { CenterIntro } from "@/components/center/Onboarding";
import { InstallBanner } from "@/components/common/Install";
import { Logo } from "@/components/common/Logo";
import { useAuth } from "@/context/AuthContext";

export const ACADEMY_URL = "https://academy.skinloversprofessional.com/";
const EXT = { target: "_blank", rel: "noopener noreferrer" };

const NAV = [
  { to: "/", label: "Home", icon: Home, end: true, id: "home" },
  { to: "/ordini", label: "Ordini", icon: ShoppingBag, id: "orders" },
  { to: "/documenti", label: "Documenti", icon: FolderOpen, id: "documents" },
  { to: "/contenuti-social", label: "Social", desktop: "Contenuti social", icon: Images, id: "social" },
  { to: "/assistenza", label: "Assistenza", icon: MessageCircle, id: "support" },
  { href: ACADEMY_URL, label: "Academy", icon: GraduationCap, id: "academy" },
  { href: "https://skinloversstudio.com/", label: "Studio", icon: Wand2, id: "studio" },
  { to: "/account", label: "Account", icon: User, id: "account", desktopOnly: true },
];

const DesktopNav = () => (
  <nav className="hidden lg:flex items-center gap-1 rounded-full border border-white/[0.07] bg-white/[0.03] p-1">
    {NAV.map((n) => n.href ? (
      <a key={n.id} href={n.href} {...EXT} data-testid={`nav-desktop-${n.id}`}
        className="px-3.5 lg:px-4 py-2 rounded-full text-[11px] uppercase tracking-[0.14em] font-medium text-gold hover:text-gold-light transition-colors duration-300">
        {n.label}
      </a>
    ) : (
      <NavLink key={n.to} to={n.to} end={n.end} data-testid={`nav-desktop-${n.id}`}
        className={({ isActive }) => `whitespace-nowrap px-3 lg:px-4 py-2 rounded-full text-[11px] uppercase tracking-[0.14em] font-medium transition-[background-color,color,box-shadow] duration-300 ${isActive ? "bg-gold/15 text-gold shadow-[0_0_18px_-4px_rgba(212,175,55,0.5)]" : "text-warm-text hover:text-white"}`}>
        {n.desktop || n.label}
      </NavLink>
    ))}
  </nav>
);

const MobileNav = () => (
  <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 px-3 pb-3 safe-bottom">
    <div className="glass grid grid-cols-7 rounded-[22px] border border-gold/20 shadow-[0_-10px_40px_-10px_rgba(0,0,0,0.9)]">
      {NAV.filter((n) => !n.desktopOnly).map((n) => n.href ? (
        <a key={n.id} href={n.href} {...EXT} data-testid={`nav-mobile-${n.id}`}
          className="relative flex flex-col items-center gap-1 pt-3 pb-2.5 text-[8px] uppercase tracking-[0.02em] text-gold">
          <n.icon className="w-5 h-5" strokeWidth={1.4} />
          <span>{n.label}</span>
        </a>
      ) : (
        <NavLink key={n.to} to={n.to} end={n.end} data-testid={`nav-mobile-${n.id}`}
          className={({ isActive }) => `relative flex flex-col items-center gap-1 pt-3 pb-2.5 text-[8px] uppercase tracking-[0.02em] transition-colors duration-300 ${isActive ? "text-gold" : "text-warm-text"}`}>
          {({ isActive }) => (
            <>
              <span className={`absolute top-0 h-[2px] w-7 rounded-full bg-gold transition-[opacity,transform] duration-300 ${isActive ? "opacity-100 scale-100 shadow-[0_0_12px_rgba(212,175,55,0.9)]" : "opacity-0 scale-50"}`} />
              <n.icon className={`w-5 h-5 transition-transform duration-300 ${isActive ? "scale-110 drop-shadow-[0_0_8px_rgba(212,175,55,0.6)]" : ""}`} strokeWidth={1.4} />
              <span>{n.label}</span>
            </>
          )}
        </NavLink>
      ))}
    </div>
  </nav>
);

export default function CenterLayout({ children }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  return (
    <div className="min-h-screen flex flex-col">
      <header className="sticky top-0 z-30 glass border-b border-white/[0.06]">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
          <Link to="/" data-testid="header-home-link" className="shrink-0"><Logo size="h-[18px] sm:h-6" /></Link>
          <DesktopNav />
          <Link to="/guida" data-testid="header-guide-link" aria-label="Guida" title="Guida"
            className="hidden lg:flex w-9 h-9 shrink-0 rounded-full border border-gold/40 bg-gold/10 items-center justify-center text-gold hover:bg-gold/20 transition-colors">
            <HelpCircle className="w-4 h-4" strokeWidth={1.6} />
          </Link>
          <div className="lg:hidden flex items-center gap-2 min-w-0">
          <Link to="/guida" data-testid="header-guide-link-mobile" aria-label="Guida"
            className="w-8 h-8 shrink-0 rounded-full border border-gold/40 bg-gold/10 flex items-center justify-center text-gold">
            <HelpCircle className="w-4 h-4" strokeWidth={1.6} />
          </Link>
          <Link to="/account" data-testid="header-account-link" className="lg:hidden flex items-center gap-2.5 text-xs text-warm-text min-w-0">
            <span className="truncate" data-testid="header-center-name">{user?.center?.center_name}</span>
            <span className="w-8 h-8 shrink-0 rounded-full border border-gold/40 bg-gold/10 flex items-center justify-center">
              <User className="w-4 h-4 text-gold" strokeWidth={1.5} />
            </span>
          </Link>
          </div>
        </div>
        <div className="gold-line opacity-40" />
      </header>
      <main key={pathname} className="flex-1 w-full max-w-5xl mx-auto px-5 sm:px-8 pt-8 sm:pt-14 pb-32 lg:pb-16 animate-page-in"><InstallBanner />{children}</main>
      <MobileNav />
      <CenterIntro />
    </div>
  );
}
